import { FileSources, ProtectedFilePayload } from "constants/sockets";
import { useSocketContext } from "context/SocketContext";
import * as React from "react";
import { audioPlayer } from "utils/audio";
import { ChatMessage } from "../types";

// Load audio
const audio = audioPlayer(
	"https://riddlestory-bucket1.s3.eu-central-1.amazonaws.com/assets/mixkit-long-pop-2358.wav",
	0.5,
	true,
);

export type ArrangedMessage = ChatMessage | number;

export function useChatRoom(roomId: string, isFocus?: boolean | null) {
	const { socket, connected } = useSocketContext();
	const [messages, setMessages] = React.useState<ChatMessage[]>([]);
	const [arrangedMessages, setArrangedMessages] = React.useState<ArrangedMessage[]>([]);
	const [unreadMessageIndex, setUnreadMessageIndex] = React.useState<number | null>(null);
	const [readTimestamp, setReadTimestamp] = React.useState<number | undefined>(undefined);

	// focusRef to persist current isFocus state value
	const focusRef = React.useRef(isFocus);

	focusRef.current = isFocus;

	const sendMessage = React.useCallback(
		(text: string) => {
			if (!socket) {
				return;
			}
			socket.emit("sendMessage", { text, roomId });
		},
		[roomId, socket],
	);

	const markAsRead = React.useCallback(() => {
		if (!socket) {
			return;
		}
		socket.emit("markChatAsRead", { roomId });
	}, [roomId, socket]);

	const [readMessages, unreadMessages] = React.useMemo<[ChatMessage[], ChatMessage[]]>(() => {
		if (!readTimestamp) {
			return [[], messages];
		}

		const readMessages = messages.filter((message) => new Date(message.timestamp).getTime() <= readTimestamp);
		const unreadMessages = messages.filter((message) => new Date(message.timestamp).getTime() > readTimestamp);
		return [readMessages, unreadMessages];
	}, [readTimestamp, messages]);

	React.useEffect(() => {
		let result: typeof arrangedMessages = [];
		result = result.concat(readMessages);
		if (!isFocus && unreadMessages.length > 0) {
			setUnreadMessageIndex(readMessages.length - 1);
			result.push(unreadMessages.length);
		} else {
			if (typeof unreadMessageIndex === "number") {
				result = result.slice(0, unreadMessageIndex).concat(result.slice(unreadMessageIndex + 1));
			}
			setUnreadMessageIndex(null);
		}
		result = result.concat(unreadMessages);
		setArrangedMessages(result);
	}, [isFocus, readMessages, unreadMessageIndex, unreadMessages]);

	React.useEffect(() => {
		if (!socket) {
			return;
		}

		const onIncomingMessage = (message) => {
			setMessages((prev) => [...prev, message]);

			// null is compared for consistency
			if (focusRef.current !== null && !focusRef.current) {
				audio.play();
				audio.setDelayedAutoPause();
			}
		};

		const onChatHistory = (messages) => {
			setMessages(messages);
		};

		const onUpdateMessageMarker = (data: { roomId: string; timestamp: number }) => {
			setReadTimestamp(data.timestamp);
		};

		const onUnlockMessage = (data: ProtectedFilePayload) => {
			if (data.fileSource !== FileSources.CHAT_ATTACHMENT) {
				// Ignore any event if the fileSource is not Chat Attachment
				return;
			}
			setMessages((prevMessages) => {
				return prevMessages.map((prevMessage) => {
					// Find message by message id
					if (prevMessage._id === data.payload._id) {
						// Replace with new message data
						return data.payload;
					}

					return prevMessage;
				});
			});
		};

		socket.on("incomingMessage", onIncomingMessage);
		socket.on("chatHistory", onChatHistory);
		socket.on("updateMessageMarker", onUpdateMessageMarker);
		socket.on("chatReadMarker", onUpdateMessageMarker);
		socket.on("updateFileAcccess", onUnlockMessage);

		socket.emit("getChatHistory", { roomId });
		socket.emit("getChatReadMarker", { roomId });

		return () => {
			socket.off("incomingMessage", onIncomingMessage);
			socket.off("chatHistory", onChatHistory);
			socket.off("updateMessageMarker", onUpdateMessageMarker);
			socket.off("sendUpdateAttachmentAccess", onUnlockMessage);
		};
	}, [roomId, socket]);

	React.useEffect(() => {
		if (!socket) {
			return;
		}
		if (connected) {
			socket.emit("getChatHistory", { roomId });
		}
	}, [connected, roomId, socket]);

	return React.useMemo(() => {
		return {
			messages,
			sendMessage,
			arrangedMessages,
			readTimestamp,
			markAsRead,
			unreadMessages,
			// updateHistoryMessages,
		};
	}, [arrangedMessages, markAsRead, messages, readTimestamp, sendMessage, unreadMessages]);
}
